import React from "react";
import { Box, Typography } from "@mui/material";
import AppleBottomNav from "./AppleBottomNav";

const systemFont = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';

function packBadge(pack) {
    const tier = String(pack?.tier || "").toLowerCase();
    if (tier === "free" || pack?.free === true) return { label: "FREE", bg: "rgba(22,163,74,0.9)" };
    if (pack?.owned === true) return { label: "OWNED", bg: "rgba(22,163,74,0.9)" };
    const price = pack?.priceUsd != null
        ? `$${Number(pack.priceUsd).toFixed(2)}`
        : pack?.price || "PREMIUM";
    return { label: String(price).toUpperCase(), bg: "rgba(202,138,4,0.92)" };
}

function CoverMosaic({ pictures = [], fallbackUrl }) {
    const tiles = pictures.filter((p) => p?.imageUrl).slice(0, 4);
    if (tiles.length === 0) {
        if (fallbackUrl) {
            return (
                <Box
                    component="img"
                    src={fallbackUrl}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
            );
        }
        return <Box sx={{ width: "100%", height: "100%", bgcolor: "rgba(15,23,42,0.08)" }} />;
    }
    return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexWrap: "wrap" }}>
            {tiles.map((pic) => (
                <Box
                    key={pic.storyId || pic.imageUrl}
                    component="img"
                    src={pic.imageUrl}
                    alt=""
                    sx={{ width: "50%", height: "50%", objectFit: "cover", display: "block" }}
                />
            ))}
        </Box>
    );
}

function PackCard({ pack }) {
    const badge = packBadge(pack);
    const count = Array.isArray(pack?.pictures)
        ? pack.pictures.length
        : Array.isArray(pack?.storyIds)
          ? pack.storyIds.length
          : Number(pack?.pictureCount) || 0;
    const fallbackUrl =
        pack?.coverUrl
        || pack?.pictures?.[0]?.imageUrl
        || null;

    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.1)",
                boxShadow: "0 4px 14px rgba(15, 23, 42, 0.08)",
            }}
        >
            <Box sx={{ position: "relative", width: "100%", aspectRatio: "1 / 1", overflow: "hidden" }}>
                <CoverMosaic pictures={pack?.pictures} fallbackUrl={fallbackUrl} />
                <Box
                    sx={{
                        position: "absolute",
                        right: 8,
                        bottom: 8,
                        px: 1,
                        py: 0.35,
                        borderRadius: "6px",
                        bgcolor: badge.bg,
                        color: "#fff",
                        fontFamily: systemFont,
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        letterSpacing: "0.04em",
                        lineHeight: 1.2,
                    }}
                >
                    {badge.label}
                </Box>
            </Box>
            <Box sx={{ px: 1.1, py: 1 }}>
                <Typography
                    sx={{
                        fontFamily: systemFont,
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        lineHeight: 1.25,
                        color: "#0f1419",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "2.5em",
                    }}
                >
                    {pack?.title || "Pack"}
                </Typography>
                <Typography
                    sx={{
                        mt: 0.35,
                        fontFamily: systemFont,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "rgba(15, 23, 42, 0.62)",
                    }}
                >
                    {count > 0 ? `${count} pictures` : "Pack"}
                </Typography>
            </Box>
        </Box>
    );
}

/**
 * App Store Premium packs tab — matches android PacksScreen + AppShell packs hero.
 */
export default function ApplePremiumPacksMenu({ packs = [] }) {
    const list = Array.isArray(packs) ? packs.slice(0, 4) : [];

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minHeight: "100%",
                bgcolor: "#FFFBF5", // Android AppShell canvas for the packs tab
                overflow: "hidden",
                position: "relative",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flexShrink: 0,
                    px: 2,
                    pt: "max(env(safe-area-inset-top, 0px), 10px)",
                    minHeight: 48,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: systemFont,
                        fontWeight: 800,
                        letterSpacing: "1.1px",
                        fontSize: "0.9375rem",
                        textTransform: "uppercase",
                        color: "#1E293B",
                    }}
                >
                    Premium packs
                </Typography>
            </Box>

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    px: 1.5,
                    pt: 0.5,
                    overflow: "hidden",
                }}
            >
                <Box sx={{ flexShrink: 0, px: 0.5, pb: 1.5 }}>
                    <Typography
                        sx={{
                            fontFamily: systemFont,
                            fontSize: "0.7rem",
                            fontWeight: 800,
                            letterSpacing: "0.12em",
                            color: "rgba(15, 23, 42, 0.72)",
                        }}
                    >
                        UNLOCK A SET
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.4,
                            fontFamily: systemFont,
                            fontSize: "1.25rem",
                            fontWeight: 800,
                            letterSpacing: "-0.02em",
                            color: "#0f1419",
                            lineHeight: 1.2,
                        }}
                    >
                        Every level. Buy once.
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.6,
                            fontFamily: systemFont,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            lineHeight: 1.35,
                            color: "rgba(15, 23, 42, 0.78)",
                        }}
                    >
                        One pack opens every piece count — Easy through Insane. No extra charge to play again.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gridAutoRows: "minmax(0, 1fr)",
                        gap: 1.25,
                        alignContent: "start",
                        overflow: "hidden",
                        pb: 1,
                    }}
                >
                    {list.map((pack) => (
                        <Box key={pack.packId || pack.id || pack.title} sx={{ minHeight: 0 }}>
                            <PackCard pack={pack} />
                        </Box>
                    ))}
                </Box>
            </Box>

            <AppleBottomNav activeTab="packs" />
        </Box>
    );
}
